import { EditorOptions, useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import Bold from '@tiptap/extension-bold';
import Underline from '@tiptap/extension-underline';
import Italic from '@tiptap/extension-italic';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import BulletList from '@tiptap/extension-bullet-list';
import Placeholder, { PlaceholderOptions } from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import CharacterCount, {
	CharacterCountOptions,
} from '@tiptap/extension-character-count';
import History from '@tiptap/extension-history';

export type UseRichTextEditorProps = Omit<
Partial<EditorOptions>,
'extensions'
> &
Partial<Pick<PlaceholderOptions, 'placeholder'>> &
Pick<CharacterCountOptions, 'limit'>;

export const linkProtocols = [
	'http',
	'https',
	'mailto',
	'tel'
];

const useRichTextEditor = ( {
	limit,
	placeholder,
	...spreadableProps
}: UseRichTextEditorProps ) => {
	const editor = useEditor(
		{
			immediatelyRender: false,
			extensions: [
				Document,
				Paragraph,
				Text,
				Bold,
				Underline,
				Italic,
				OrderedList,
				ListItem,
				BulletList,
				Link.configure( {
					HTMLAttributes: {
						rel: 'noopener noreferrer nofollow',
					},
					openOnClick: false,
					autolink: true,
					defaultProtocol: 'https',
					protocols: linkProtocols,
					validate: ( href ) =>
						/^https?:\/\//.test( href ) ||
						/^mailto:/.test( href ) ||
						/^tel:/.test( href ),
					shouldAutoLink: ( url ) => {
						try {
							// Try to construct URL: if successful then it's a valid URL, and if it throws an error then it's not a valid URL
							if ( url.includes( ':' ) ) {
								new URL( url );
							} else {
								new URL( `https://${ url }` );
							}

							return true;
						} catch {
							return false;
						}
					},
				} ),
				CharacterCount.configure( { limit } ),
				History,
				Placeholder.configure( { placeholder } ),
			],
			editorProps: {
				...spreadableProps?.editorProps,
				transformPastedHTML: ( html: string ) => {
					// Create a temporary div to parse and sanitize the HTML
					const tempDiv = document.createElement( 'div' );
					tempDiv.innerHTML = html;

					// Find all links and sanitize them
					const links = tempDiv.querySelectorAll( 'a[href]' );
					links.forEach( ( link ) => {
						const href = link.getAttribute( 'href' );
						const linkText = link.textContent || link.innerHTML;

						if ( href ) {
							// Create a new sanitized link with only href and secure rel
							const sanitizedLink = document.createElement( 'a' );
							sanitizedLink.setAttribute( 'href', href );
							sanitizedLink.setAttribute( 'rel', 'noopener noreferrer nofollow' );
							sanitizedLink.innerHTML = linkText;

							// Replace the original link with the sanitized one
							link.parentNode?.replaceChild( sanitizedLink, link );
						}
					} );

					// Return the sanitized HTML
					return tempDiv.innerHTML;
				},
			},
			...spreadableProps,
		},
		[]
	);
	return editor;
};

export default useRichTextEditor;
